import React, { useEffect, useState, useMemo } from "react";
import { kcFetch, API_BASE } from "../../lib/apiClient";
import { useAppSettings, resolveLogo } from "../../lib/AppSettingsContext";

// ============================================================
// PublicDonationsListingsPage — /donations/listings
// No-auth public browsing of currently available donated items.
// Same dark-theme Kingdom Connect styling as the other QR-poster
// public destinations (Welcome Form, Public Booking, Food Form).
// Sanitized payload: no donor name, no internal notes.
// ============================================================
export default function PublicDonationsListingsPage() {
  const { settings: appSettings, loaded: appSettingsLoaded } = useAppSettings();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await kcFetch(`${API_BASE}/public/donations/listings`);
        if (r.ok) setListings(await r.json());
      } catch (_) { /* offline tolerant */ }
      setLoading(false);
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return listings;
    return listings.filter(l =>
      (l.itemName || "").toLowerCase().includes(q) ||
      (l.category || "").toLowerCase().includes(q) ||
      (l.shortDescription || "").toLowerCase().includes(q)
    );
  }, [listings, query]);

  return (
    <div className="pdl-page" data-testid="public-listings-page">
      <style>{CSS}</style>
      <div className="pdl-shell">
        <div className="pdl-topbar">
          <img src={resolveLogo(appSettings.appLogo)} alt={appSettings.appName} className="pdl-logo" />
          <div>
            <div className="pdl-brand" data-testid="public-listings-app-name">{appSettings.appName}</div>
            <div className="pdl-sub">Donations · Available Items</div>
          </div>
        </div>

        <div className="pdl-hero">
          <h1>Available Donations</h1>
          <p>Browse items currently available from our compassion team. Tap an item for details.</p>
        </div>

        <input
          className="pdl-search"
          data-testid="public-listings-search"
          placeholder="Search items, category, description…"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />

        {loading ? (
          <div className="pdl-empty" data-testid="public-listings-loading">Loading listings…</div>
        ) : filtered.length === 0 ? (
          <div className="pdl-empty" data-testid="public-listings-empty">
            {query ? "No listings match your search." : "There are no items available right now. Please check back soon — God bless."}
          </div>
        ) : (
          <div className="pdl-grid" data-testid="public-listings-grid">
            {filtered.map(it => (
              <div key={it.id} className="pdl-card" data-testid={`public-listing-${it.id}`}>
                <div className="pdl-card-head">
                  <span className="pdl-cat">{it.category || "Other"}</span>
                  <span className="pdl-date">{it.dateReceived || ""}</span>
                </div>
                <div className="pdl-card-name">{it.itemName || "Item"}</div>
                {it.shortDescription && <div className="pdl-card-desc">{it.shortDescription}</div>}
                <div className="pdl-card-foot">
                  <span className="pdl-status">{it.status || "Available"}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="pdl-foot">
          <span>{appSettings.appName}</span>
          <span aria-hidden="true">·</span>
          <span>{appSettings.appTagline}</span>
        </div>
      </div>
    </div>
  );
}

const CSS = `
.pdl-page { min-height: 100vh; background: var(--kc-surface); padding: 28px 18px 64px; font-family: 'Montserrat',sans-serif; color: var(--kc-text); }
.pdl-shell { max-width: 880px; margin: 0 auto; }
.pdl-topbar { display: flex; align-items: center; gap: 14px; padding-bottom: 18px; margin-bottom: 22px; border-bottom: 1px solid color-mix(in srgb, var(--kc-accent) 20%, transparent); }
.pdl-logo { width: 64px; height: 64px; object-fit: cover; border-radius: 10px; border: 1px solid color-mix(in srgb, var(--kc-accent) 40%, transparent); background: #111; }
.pdl-brand { font-family: 'Cinzel',serif; font-size: 18px; font-weight: 700; color: var(--kc-accent); letter-spacing: 0.06em; text-transform: uppercase; }
.pdl-sub { font-size: 11px; color: var(--kc-sub); letter-spacing: 0.08em; text-transform: uppercase; margin-top: 4px; }
.pdl-hero { text-align: center; margin-bottom: 22px; }
.pdl-hero h1 { font-family: 'Cinzel',serif; font-size: 28px; color: var(--kc-text); margin: 0 0 8px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; }
.pdl-hero p { font-size: 14px; color: var(--kc-sub); margin: 0; max-width: 540px; margin: 0 auto; line-height: 1.6; }
.pdl-search { width: 100%; box-sizing: border-box; background: #141414; color: var(--kc-text); border: 1px solid color-mix(in srgb, var(--kc-accent) 35%, transparent); border-radius: 12px; padding: 13px 16px; font-family: 'Montserrat',sans-serif; font-size: 14px; margin-bottom: 22px; }
.pdl-search:focus { outline: none; border-color: var(--kc-accent); box-shadow: 0 0 0 3px color-mix(in srgb, var(--kc-accent) 22%, transparent); }
.pdl-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 14px; }
.pdl-card { background: var(--kc-surface); border: 1px solid color-mix(in srgb, var(--kc-accent) 25%, transparent); border-radius: 12px; padding: 16px; transition: border-color 0.15s, transform 0.15s; }
.pdl-card:hover { border-color: var(--kc-accent); transform: translateY(-2px); }
.pdl-card-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.pdl-cat { font-size: 10px; font-weight: 700; letter-spacing: 0.10em; text-transform: uppercase; color: var(--kc-accent); background: color-mix(in srgb, var(--kc-accent) 14%, transparent); padding: 4px 10px; border-radius: 999px; border: 1px solid color-mix(in srgb, var(--kc-accent) 35%, transparent); }
.pdl-date { font-size: 11px; color: var(--kc-meta); letter-spacing: 0.05em; }
.pdl-card-name { font-family: 'Cinzel',serif; font-size: 16px; font-weight: 700; color: var(--kc-text); letter-spacing: 0.04em; margin-bottom: 6px; line-height: 1.3; }
.pdl-card-desc { font-size: 13px; color: var(--kc-sub); line-height: 1.5; margin-bottom: 10px; }
.pdl-card-foot { display: flex; justify-content: flex-end; padding-top: 8px; border-top: 1px solid color-mix(in srgb, var(--kc-accent) 12%, transparent); }
.pdl-status { font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: #4ade80; }
.pdl-empty { background: var(--kc-surface); border: 1px dashed color-mix(in srgb, var(--kc-accent) 30%, transparent); border-radius: 12px; padding: 40px 20px; text-align: center; color: var(--kc-sub); font-size: 14px; line-height: 1.6; }
.pdl-foot { margin-top: 36px; padding-top: 18px; border-top: 1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent); text-align: center; font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--kc-meta); display: flex; flex-wrap: wrap; justify-content: center; gap: 8px; }
@media (max-width: 540px) {
  .pdl-hero h1 { font-size: 22px; }
  .pdl-grid { grid-template-columns: 1fr; }
}
`;
